import { ApplicationConfig, ErrorHandler, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import {
  provideKeycloak,
  createInterceptorCondition,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  ProvideKeycloakOptions,
} from 'keycloak-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { traceInterceptor } from './core/interceptors/trace.interceptor';
import { authErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { tenantInterceptor } from './core/interceptors/tenant.interceptor';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { ReportingMessageService } from './core/services/reporting-message.service';
import { MessageService } from 'primeng/api';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { SoftwaholicPreset } from '../styles/mypreset';
import { environment } from '../environments/environment';
import { RealmChoice, activateRealm } from './core/config/platform-realm';

/**
 * URLs the bearer token is attached to: the API base and anything under it,
 * nothing else. Built from the runtime API host so a lookalike host or a
 * different scheme never receives the JWT.
 *
 * The two realms' tokens never cross. A back-office page load holds a platform
 * token, which reaches /platform and nothing else; every other page load holds
 * a business token, for which /platform is excluded — the backend answers it
 * 401 anyway, and a token sent where it cannot be used is a token leaked.
 */
export function bearerUrlPattern(apiBaseUrl: string, platformRealm = false): RegExp {
  const escaped = apiBaseUrl.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
  return platformRealm
    ? new RegExp(`^${escaped}/platform(/.*)?$`, 'i')
    : new RegExp(`^${escaped}(?!/platform(?:/|$))(/.*)?$`, 'i');
}

/**
 * How the app authenticates: identical for both realms apart from who issues
 * the token, so a platform operator and a shop's staff each meet their own
 * realm's login and never the other's.
 */
export function keycloakOptions(realm: RealmChoice): ProvideKeycloakOptions {
  return {
    config: {
      url: environment.keycloakUrl,
      realm: realm.realm,
      clientId: realm.clientId,     // SPA client in Keycloak, per environment
    },
    initOptions: {
      onLoad: 'login-required', // force login before app loads
      checkLoginIframe: false
    },
    features: [
      withAutoRefreshToken({
        // 🕒 12 hours inactivity (in ms)
        sessionTimeout: 12 * 60 * 60 * 1000, // 43200000
        onInactivityTimeout: 'logout',       // auto-logout when user is inactive
      }),
    ],
    providers: [AutoRefreshTokenService, UserActivityService]
  };
}

/**
 * Built after loadRuntimeConfig() has run (see main.ts): the Keycloak client
 * and the bearer pattern depend on values served per environment.
 *
 * The URL decides the realm, because one Keycloak instance holds one realm:
 * the path is already known here, and the flags that gate the back office were
 * read with the rest of config.json a moment ago.
 */
export function buildAppConfig(pathname: string = location.pathname): ApplicationConfig {
  //#region Choosing this page load's realm from the URL
  const realm = activateRealm(pathname);
  //#endregion

  //#region Attaching the token only to our own API, and only to this realm's half of it
  const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: bearerUrlPattern(environment.apiBaseUrl, realm.isPlatform),
    bearerPrefix: 'Bearer',
  });
  //#endregion

  //#region Assembling the providers around the runtime identity settings
  return {
    providers: [
      provideKeycloak(keycloakOptions(realm)),

      {
        provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
        useValue: [urlCondition],
      },

      provideBrowserGlobalErrorListeners(),
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes),

      // Order: first add bearer, then error handling
      provideHttpClient(
        withInterceptors([
          // First: everything the other interceptors modify still carries the
          // trace and session headers.
          traceInterceptor,
          includeBearerTokenInterceptor,
          tenantInterceptor,
          authErrorInterceptor,
        ])
      ),

      ConfirmationService,

      // Substituted once, reaching every messageService.add(...) call site
      // without touching one of them. Works only because no component
      // declares providers: [MessageService] any more — that would give it a
      // private plain instance and silently skip reporting.
      { provide: MessageService, useClass: ReportingMessageService },
      provideAnimationsAsync(),
      providePrimeNG({
        theme: {
          preset: SoftwaholicPreset,
          options: {
            darkModeSelector: '[data-mode="dark"]',
          },
        },
      }),
    ],
  };
  //#endregion
}
